import { debounce } from 'lodash';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';

import { api } from '../../data/api';
import { deregisterListener, registerListener } from '../../store/middleware/pubsub';

import { onboardingCompleteFact } from '../../data/entities/fact/selectors';
import { IPromotion, IPromotionOpportunity } from '../../data/entities/promotions';
import { actions as entityActions } from '../../data/store';
import { IListableContract } from '../../entities/contracts/types';
import { logging } from '../../logging';
import { IThunkDispatch } from '../../store/middleware';
import { IState } from '../../store/reducer';
import { formatPromotionForSend } from '../../view/components/property-shell-modal/helpers';

export const useDataLoad = (shouldLoad: boolean, loadData: () => Promise<any>) => {
	const [loading, setLoading] = useState(shouldLoad);
	const [requested, setRequested] = useState(false);

	useEffect(() => {
		if (!shouldLoad) return;
		if (requested) return;

		setRequested(true);
		loadData().catch(() => null).then(() => setLoading(false));
	});

	return loading;
};

export interface IAsyncStatus {
	result: any;
	error: any;
	isLoading: boolean;
}

export const useAsyncRequest = (issue: () => Promise<any>): [IAsyncStatus, () => Promise<void>] => {
	const [status, setStatus] = useState({ result: null, error: null, isLoading: false });

	const issueRequest = useCallback(async () => {
		setStatus({ result: null, error: null, isLoading: true });

		try {
			const result = await issue();
			setStatus({ result, error: null, isLoading: false });
		} catch (error) {
			setStatus({ result: null, error, isLoading: false });
		}
	}, [issue]);

	return [status, issueRequest];
};

/**
 * A React hook which applies lodash.debounce to a function correctly.
 * The wrapped function will be invoked only after `wait` milliseconds
 * have passed since the last invocation of the returned function.
 * On dismount, if there are pending invocations, they are flushed.
 *
 * @param fn The function to debounce.
 * @param wait The number of milliseconds to delay.
 */
export const useDebouncedCallback = <F extends (...a: any[]) => any>(fn: F, wait: number) => {
	// Memoizing with [] ensures these are only run when the component mounts or dismounts
	const debounced = useCallback(debounce(fn, wait, { trailing: true }), []);
	// No-op effect which returns flush for cleanup
	useEffect(() => debounced.flush, []);
	return debounced;
};

export const useReduxAction = (actionType: any, callback: (action: Action) => any) => {
	useEffect(() => {
		const key = Math.random().toString(36);

		registerListener(key, actionType, callback);

		return () => deregisterListener(key, actionType);
	});
};

/**
 * Hook for applying a transformation on update of the state
 *
 * @param initialState - the initial value to be transformed
 * @param transformation - a transformation function
 *
 * @return an array containing the transformed value of the state as well as a function to update the state by applying the transformation
 */
export const useTransformState = <S>(initialState: S, transformation: (s: S) => S): [S, (s: S) => void] => {
	const [transformedValue, seTransformedValue] = useState(transformation(initialState));
	const transformState = (data: S) => {
		seTransformedValue(transformation(data));
	};
	return [transformedValue, transformState];
};

export const useCanonicalId = (propertyId: string | number) => {
	const looksLegacy = (parseInt(String(propertyId), 10) != propertyId); // tslint:disable-line
	const [canonicalId, setCanonicalId] = useState<number | null>(looksLegacy ? null : parseInt(String(propertyId), 10));
	const [legacyId, setLegacyId] = useState<string | null>(looksLegacy ? propertyId as string : null);
	const [errored, setErrored] = useState(false);
	const loading = useDataLoad(looksLegacy && !canonicalId, async () => {
		try {
			const { id, legacy_property_id } = await api.properties.getById(propertyId);
			setCanonicalId(id);
			setLegacyId(legacy_property_id);
		} catch (e) {
			logging.error(`Failed to fetch canonical id for input ${propertyId}`, e);
			setErrored(true);
		}
	});
	return { loading, id: canonicalId, legacy: legacyId, errored };
};

export const useOnboardingComplete = (ownerId?: string) => {
	const dispatch: IThunkDispatch = useDispatch();
	const onboardingFact = !ownerId ? null : useSelector((state: IState) => onboardingCompleteFact(state, ownerId));
	const loadingFact = useDataLoad(!!ownerId && !onboardingFact, async () => await dispatch(entityActions.facts.getByAccount(ownerId!)));
	// const [onboardingComplete, setOnboardingComplete] = React.useState<boolean | null>(null);
	// const [propertyId, setPropertyId] = React.useState<number | null>(null);
	const onboardingComplete = onboardingFact?.context?.complete === true;
	const propertyId = (onboardingFact?.context.propertyId || null);

	// React.useEffect(() => {
	// 	if (!loadingFact && onboardingFact) {
	// 		const guaranteedIncomplete = onboardingFact && (onboardingFact.context.complete === false);
	// 		setOnboardingComplete(!guaranteedIncomplete);
	// 	}
	// }, [loadingFact, onboardingFact]);

	return [loadingFact, onboardingComplete, propertyId] as [boolean, boolean | null, number | null];
};

export const usePropertyPromotions = (propertyId?: number) => {
	const dispatch: IThunkDispatch = useDispatch();
	const isLoading = useDataLoad(!!propertyId, async () => await dispatch(entityActions.promotionOpportunities.getPromotionOpportunities(propertyId!)));
	const promotions = !propertyId ? [] : useSelector((state: IState) => state.data.promotionOpportunities) as IPromotionOpportunity[];
	return [promotions, isLoading] as [IPromotionOpportunity[], boolean];
};

export const useCustomerChannels = (ownerId?: string) => {
	const dispatch: IThunkDispatch = useDispatch();
	const isLoading = useDataLoad(!!ownerId, async () => await dispatch(entityActions.listingContracts.getByCustomer(ownerId!)));
	const channels = !ownerId ? [] : useSelector((state: IState) => state.data.listingContracts.byCustomerId[ownerId!]);
	return [channels, isLoading] as [IListableContract[], boolean];
};

export const useAutoSavePromotion = (propertyId: number) => {
	const [promotionSaved, setPromotionSaved] = useState<IPromotion[]>([]);
	const [promotion, setPromotion] = React.useState<IPromotionOpportunity | null>(null);
	React.useEffect(() => {
		if (promotionSaved && promotion !== null) {
			const alreadySavedPromo = promotionSaved.find((p) => p.internal_reference === promotion.internal_reference);
			if (!alreadySavedPromo) {
				api.promotions.createPromotionForProperty(propertyId, formatPromotionForSend(propertyId, promotion)).then((promo) => {
					setPromotionSaved([promo, ...promotionSaved]);
				});
			} else {
				api.promotions.deletePromotionForProperty(propertyId, alreadySavedPromo);
				setPromotionSaved(promotionSaved.filter((p) => p.internal_reference !== promotion.internal_reference));
			}
		}
	}, [promotion]);

	const updatePromotion = (promo: IPromotionOpportunity) => {
		setPromotion({ ...promo });
	};
	return [updatePromotion, promotionSaved] as [(promotion: IPromotionOpportunity) => void, IPromotion[]];
};
